import { BasicButton, InputTextArea, Loading, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useCaseMutation from "@/hooks/useCaseMutation";
import { LETTER_TYPE_VALUE } from "@/libraries/constant";
import { getCaseLetter, getCaseLetterPdfFile } from "@/queries";
import { EmailListSchema, SendCaseLetterFormFieldSchema } from "@/schemas";
import { usePrompt } from "@/stores/prompt";
import { zodResolver } from "@hookform/resolvers/zod";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/letter/$letterId/export",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const caseId = Route.useParams({
		select: (state) => state.id,
	});
	const letterId = Route.useParams({
		select: (s) => s.letterId,
	});
	const {
		data: { case_letter },
	} = useSuspenseQuery(getCaseLetter(letterId));
	const {
		watch,
		control,
		handleSubmit,
		formState: { isValid, isDirty, isSubmitting },
	} = useForm<SendCaseLetterFormFieldSchema>({
		mode: "all",
		resolver: zodResolver(SendCaseLetterFormFieldSchema),
		defaultValues: {
			id: letterId,
			emails: "",
			note: "",
		},
	});

	const watchEmail = watch("emails");
	const parseEmail = EmailListSchema.safeParse(watchEmail);

	const openPrompt = usePrompt((state) => state.open);
	const { sendLetter } = useCaseMutation();
	const casePdf = useSuspenseQuery(getCaseLetterPdfFile(letterId));

	const onSubmit: SubmitHandler<SendCaseLetterFormFieldSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		return openPrompt({
			title: "I have permission / consent to perform this task.",
			positiveButton: {
				label: "Yes",
				variant: "contained",
				color: "primary",
				onClick: () => {
					if (!parseEmail.success) {
						return toast.error(
							"Invalid email format detected. Please check the recipient email addresses.",
						);
					}
					return sendLetter.mutateAsync(data, {
						onSuccess: () => {
							navigate({
								to: "/dashboard/case/view/$id/letter/$letterId",
								params: {
									id: caseId,
									letterId,
								},
							});
							return toast.success("Letter sent successfully");
						},
						onError: () => {
							return toast.error("Failed to export into email");
						},
					});
				},
			},
			negativeButton: {
				label: "Cancel",
				variant: "outlined",
				color: "primary",
			},
		});
	};

	return (
		<>
			<BackAppBar
				title={`Send ${LETTER_TYPE_VALUE[case_letter.letter_type]} as Email`}
				className="pb-3"
			>
				<Alert
					icon={<InfoRoundedIcon className="!fill-primary" />}
					severity="info"
					className="!bg-blue-100"
				>
					<Typography fontSize={14}>
						You are about to send this letter to the specified recipients
					</Typography>
				</Alert>
			</BackAppBar>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container bg-white flex flex-col flex-1"
			>
				<div className="flex flex-col flex-1 py-4 gap-6">
					<Controller
						control={control}
						name="emails"
						render={({ field }) => (
							<TextField
								id={field.name}
								type="text"
								label="Recipient Email(s)"
								helperText="Enter one or multiple email addresses separated by commas"
								error={!parseEmail.success}
								className="[&_.MuiFormLabel-asterisk]:text-red-500 [&_.MuiInputBase-root]:!rounded-xl"
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<Controller
						control={control}
						name="note"
						render={({ field, fieldState }) => (
							<div>
								<InputTextArea
									fullWidth
									rows={4}
									maxRows={4}
									label="Note (Recommended)"
									maxLength={200}
									error={!!fieldState.error?.message}
									maxLengthClassName="!bottom-0"
									{...omit(field, ["ref"])}
								/>
								<Typography
									fontWeight={400}
									fontSize={12}
									className="pl-4 text-[#00000099]"
								>
									Add a personalized message to accompany the{" "}
									{LETTER_TYPE_VALUE[case_letter.letter_type]}
								</Typography>
							</div>
						)}
					/>
					{casePdf.data ? (
						<div className="w-fit space-y-2">
							<Typography fontSize={14}>Attachment</Typography>
							<div className="w-fit border border-gray-100 rounded-xl">
								<IconButton
									LinkComponent="a"
									target="_blank"
									className="!rounded-xl"
									href={URL.createObjectURL(casePdf.data)}
									title={`Summary of Medical Report ${caseId}.pdf`}
								>
									<InsertDriveFileRoundedIcon />
								</IconButton>
							</div>
						</div>
					) : null}
				</div>
				<div className="container py-4 shadow">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						className="w-full !h-12 text-sm font-semibold !shadow-none"
					>
						Submit
					</BasicButton>
				</div>
			</form>
			<Loading status={sendLetter.isPending} />
		</>
	);
}
